import { Injectable, Inject, PLATFORM_ID, REQUEST_CONTEXT, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { environment } from '../../../environments/environment';

export interface LogData {
  site_name: string;
  site_url: string;
  level: string; // 'INFO' | 'ERROR' | 'NOTICE' | 'WARNING' | 'DEBUG' | 'CRITICAL' | 'ALERT' | 'EMERGENCY'
  message: string;
  log_timestamp: string;
  environment?: string;
  url?: string;
  method?: string;
  ip?: string;
  agent?: string;
  file?: string;
  line?: number;
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private apiUrl = environment.loggerApiUrl;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(REQUEST_CONTEXT) private requestContext?: any
  ) { }

  log(level: string, message: string, extra: Partial<LogData> = {}) {
    let domainName = this.requestContext?.domainName || (this.document?.location?.hostname ? this.document.location.hostname : 'Unknown');
    let siteName = domainName;
    let siteUrl = this.requestContext?.siteUrl || (this.document?.location?.origin && this.document.location.origin !== 'null' ? this.document.location.origin : (domainName !== 'Unknown' ? `https://${domainName}` : 'Unknown'));
    let url = this.document?.location?.href || 'Unknown';
    let agent = 'Unknown';

    let clientIp = 'Unknown';
    if (isPlatformBrowser(this.platformId)) {
      agent = window.navigator.userAgent || agent;
    } else {
      if (this.requestContext && this.requestContext.clientIp) {
        clientIp = this.requestContext.clientIp;
      }
    }

    const logData: LogData = {
      site_name: siteName,
      site_url: siteUrl,
      level,
      message,
      log_timestamp: new Date().toISOString(),
      environment: environment.production ? 'production' : 'staging',
      url,
      agent,
      ip: clientIp !== 'Unknown' ? clientIp : undefined,
      ...extra
    };

    // Only send the log to the API if running on the server side (SSR)
    if (!isPlatformBrowser(this.platformId)) {
      // Fire and forget
      this.http.post(this.apiUrl, logData).subscribe({
        next: () => {},
        error: (err) => {
          console.error('Failed to send log to API', err);
        }
      });
    } else {
      // If we are in the browser, you might want to just console.error or do nothing
    }
  }

  error(message: string, extra: Partial<LogData> = {}) {
    this.log('ERROR', message, extra);
  }

  info(message: string, extra: Partial<LogData> = {}) {
    this.log('INFO', message, extra);
  }

  warning(message: string, extra: Partial<LogData> = {}) {
    this.log('WARNING', message, extra);
  }

  debug(message: string, extra: Partial<LogData> = {}) {
    this.log('DEBUG', message, extra);
  }
  notice(message: string, extra: Partial<LogData> = {}) {
    this.log('NOTICE', message, extra);
  }
}
